import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import {
  lightenColor,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import { LegendKey, BrushStart } from "../../../models/chart_model";
import { SearchBox } from "../../../components/Search";
import { stringToConsistentColor } from "../../../extensions/string";
import { HarayanaDiscomData } from "../../../models/rajasthan";
import { getHaryanaDiscomData, getHaryanaLoadMetrics } from "../../../Rest_api/RestApi";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import MetricChip from "../../../components/Metrics";
import { DiscomLoadMetrics } from "../../../models/metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { H_CHART_TYPES } from "../const";
import { GROUP_BY, DataTabs } from "./Data";
import { getMonthFromDate } from "../../../extensions/date";
import { renderHourTick } from "../../../components/recharts/components";
import { FilterTab } from "../../../components/Filters";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { HaryanaSummaryAnalysis } from "./Discom_Analysis_Summary";
import { HaryanaPeakAnalysis } from "./Peak_Discom";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { normalizeDisplayLabelWithoutUndefined } from "../../../utility/displayText";

let Intervals = [
  {
    label: "Time Slot",
    key: "slot",
    values: ["block", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" }
];

const UNITS = ["MW", "MWh", "LU"];

// Helper function to get month with year (e.g., "Oct-24" instead of just "Oct")
export function getMonthWithYear(dateString: string): string {
  const date = new Date(dateString);
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear().toString().slice(-2); // Get last 2 digits of year
  return `${month}-${year}`;
}

export function HaryanaDiscomAnalysis() {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );

  const [selectedXAxisMode, setSelectedXAxisMode] = useState(X_AXIS_MODES[0]);

  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [data, setData] = useState<HarayanaDiscomData[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedDiscom, setSelectedDiscom] = useState<any[]>([]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[1]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [loading, setLoading] = useState(true);
  // Track which legends are selected in the chart (empty = all selected)
  const [activeLegends, setActiveLegends] = useState<LegendKey[]>([]);
  const [metrics, setMetrics] = useState<DiscomLoadMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  const chartSuffixes = [
    { suffix: "demand", displayName: "Load", stackSuffix: "demand" },
  ];

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    selectedInterval: { isTime: boolean },
    selectedChartType: { value: string; stackId?: string },
    ExchangeColors: string[]
  ) => {
    return {
      dataKey: `${key}_${suffix}_${selectedUnit.toLowerCase()}`,
      name: `${normalizeDisplayLabelWithoutUndefined(key)} ${displayName}`.trim(),
      legendColor: undefined,
      type: selectedChartType.value,
      stackId:
        selectedChartType.stackId !== undefined ? stackSuffix : undefined,
    };
  };

  const [selectedSuffix, setSelectedSuffix] = useState([chartSuffixes[0]]);

  const _handlefetchData = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultTrendDates) {
      return;
    }

    setLoading(true);

    try {
      const promises = DefaultDates[activeTab].defaultTrendDates.map(async (date: any) => {
        try {
          const [startDate, endDate] = date.value.split(",");
          const data = await getHaryanaDiscomData(startDate, endDate);
          return data.map((item) => ({
            ...item,
            dates: date.label,
          }));
        } catch (error) {
          console.error(`Error processing date ${date.label}:`, error);
          return [];
        }
      });

      const results = await Promise.all(promises);
      const flattenedData = results.flat();

      // Deduplicate data based on date and block combination
      // This prevents duplicates when overlapping date ranges are selected
      const dataMap = new Map<string, HarayanaDiscomData>();
      flattenedData.forEach((item) => {
        const uniqueKey = `${item.date}_${item.block}`;
        if (!dataMap.has(uniqueKey)) {
          dataMap.set(uniqueKey, item);
        }
      });
      const newData = Array.from(dataMap.values());

      setData(newData);

      setUniqueDate(
        Array.from(
          new Set(
            newData
              .map((item) => item.date)
              .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
          )
        )
      );
      setUniqueBlock(Array.from(new Set(newData.map((item) => item.block))));
    } catch (error) {
      console.error("Error in data fetching:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab]);

  useEffect(() => {
    // FIXED: Use getMonthWithYear instead of getMonthFromDate to include year
    let filteredData = data.map((item) => ({
      ...item,
      month: getMonthWithYear(item.date), // This now returns "Oct-24" instead of just "Oct"
    }));

    if (selectedDate.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDate.map((item) => item.value).includes(item.date)
      );
    }
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((item) => item.value).includes(item.block)
      );
    }

    // FIXED: Sort filtered data in ascending order by date and block
    filteredData.sort((a, b) => {
      // First sort by date
      const dateCompare = new Date(a.date).getTime() - new Date(b.date).getTime();
      if (dateCompare !== 0) return dateCompare;

      // Then sort by block if dates are the same
      return a.block - b.block;
    });

    function getKeys() {
      if (selectedInterval.key === "slot") {
        return ["date", "block"];
      } else if (selectedInterval.key === "date") {
        return ["date"];
      } else {
        return [selectedInterval.values[0]];
      }
    }

    const par: ChartDataParams = {
      keys: getKeys(),
      groupBy: [],
      tableKeys: ["date", "block"],
      volumeKey: ``,
      aggregationKeys: ["demand_mw", "demand_mwh", "demand_lu"],
      priceKey: "",
      averageKeys: ["demand_mw"],
      averageDuplicationKeys: ["date", "block"],
      data: filteredData,
    };
    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
    setTableData(cData.tableData || []);
  }, [
    data,
    selectedGroupKeys,
    selectedInterval,
    selectedDiscom,
    selectedArea,
    selectedDate,
    selectedBlock,
    selectedSuffix,
    selectedUnit,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  const fetchMetrics = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultTrendDates) {
      setMetrics(null);
      setMetricsLoading(false);
      return;
    }

    setMetricsLoading(true);
    try {
      const dates = DefaultDates[activeTab].defaultTrendDates;

      const ranges = dates.map((date: any) => {
        const [startDate, endDate] = date.value.split(",");
        return {
          startDate: startDate,
          endDate: endDate,
          blocks: selectedBlock.map((block) => block.value),
        };
      });

      const metricsData = await getHaryanaLoadMetrics({
        ranges: ranges,
        aggregated: true,
      });

      setMetrics(Array.isArray(metricsData) ? metricsData[0] : metricsData);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [DefaultDates, activeTab, selectedBlock]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  function handleUnitChange(params: string) {
    setSelectedUnit(params);
  }

  function handleXAxisModeChange(item: string, index: number) {
    setSelectedXAxisMode(X_AXIS_MODES[index]);
  }

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        if (selectedInterval.isTime) {
          return "block";
        } else {
          return selectedInterval.values[0];
        }
      }
      return "block";
    } else {
      return selectedInterval.key;
    }
  }, [
    selectedBlock.length,
    selectedInterval.isTime,
    selectedInterval.key,
    selectedInterval.values,
  ]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  function secondDataKey() {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        return "date";
      }
      return "date";
    } else {
      return undefined;
    }
  }

  const summaryExtraKeys = selectedInterval.isTime
    ? [
      "block",
      selectedInterval.values[1],
    ]
    : [selectedInterval.values[0]];

  const summaryExtraLabels = selectedInterval.values.map(
    (value) => (value === "date" ? "Date" : value)
  );

  // For summary/table: use only the selected (active) legends, or all if none selected
  const allLegends = getLegends();
  const visibleLegends =
    activeLegends.length > 0
      ? allLegends.filter((l) =>
        activeLegends.some((al: any) => al.dataKey === l.dataKey)
      )
      : allLegends;

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Haryana Load",
      dates: DefaultDates[activeTab]?.defaultTrendDates || [],
    }),
  };

  // Show Peak Analysis component when selected
  if (selectedXAxisMode.value === "peak") {
    return (
      <HaryanaPeakAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={handleUnitChange}
        metrics={metrics}
        metricsLoading={metricsLoading}
      />
    );
  }

  // Show Summary component when selected
  if (selectedXAxisMode.value === "summary") {
    return (
      <HaryanaSummaryAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={handleUnitChange}
        metrics={metrics}
        metricsLoading={metricsLoading}
      />
    );
  }

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={[selectedXAxisMode.label]}
            onSelect={handleXAxisModeChange}
          />

          <FilterMenu
            title="Intervals"
            items={Intervals.map((item) => item.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(item, index) => {
              setSelectedInterval(Intervals[index]);
            }}
          />

          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              handleUnitChange(item);
            }}
          />

          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(H_CHART_TYPES[index]);
            }}
          />

          {/* Disabled Filters */}
          <FilterMenu
            title="Discom"
            items={["UHBVN", "DHBVN"]}
            selectedItems={[]}
            disabled={true}
          />

          <FilterMenu
            title="Group By"
            items={["Discom Load", "Total Discom Load", "HVPN Drawal"]}
            selectedItems={[]}
            disabled={true}
          />

          <FilterMenu
            title="Type"
            items={["Schedule", "Drawal", "Load Shedding", "Total Load"]}
            selectedItems={[]}
            disabled={true}
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        <div className="flex flex-wrap gap-4 my-0 justify-center">
          <MetricChip
            icon={<PeakIcon width={20} height={19} />}
            items={[
              {
                label: "Peak Load",
                value: metrics?.["Peak Load (MW)"]?.value ?? undefined,
                unit: metrics?.["Peak Load (MW)"]?.unit ?? "",
              },
            ]}
          />
          <MetricChip
            icon={<AvgIcon width={20} height={19} />}
            items={[
              {
                label: "Average Load",
                value: metrics?.["Average Load (MW)"]?.value ?? undefined,
                unit: metrics?.["Average Load (MW)"]?.unit ?? "",
              },
            ]}
          />
          <MetricChip
            icon={<MinIcon width={20} height={19} />}
            items={[
              {
                label: "Minimum Load",
                value: metrics?.["Minimum Load (MW)"]?.value ?? undefined,
                unit: metrics?.["Minimum Load (MW)"]?.unit ?? "",
              },
            ]}
          />
          <MetricChip
            icon={<TotalIcon width={20} height={19} />}
            items={[
              {
                label: "Total Energy",
                value: metrics?.["Total Energy (MU)"]?.value ?? undefined,
                unit: metrics?.["Total Energy (MU)"]?.unit ?? "",
              },
            ]}
          />
        </div>
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Haryana Load"
              subtitle="Fetching HPPC discom drawal and schedule data…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-60">
                  Haryana Load ({selectedUnit})
                </h2>
                <div className="flex gap-x-6  justify-end">
                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Select Block"
                    selectedOptions={selectedBlock.map((item) => item.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    onClick={() => {
                      dataTab.label === "Chart" &&
                        downloadChartTableCSV(tablePar);
                      dataTab.label === "Summary" &&
                        downloadSummaryCSV(tablePar);
                      dataTab.label === "Table" &&
                        downloadChartTableCSV(tablePar);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  showBrush={chartData.length > 96 ? true : undefined}
                  brushStart={BrushStart.Start}
                  isTimeSlot={selectedInterval.isTime}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={getLegends()}
                  xDataKey={xDataKey}
                  xTick={
                    selectedInterval.isTime && selectedBlock.length === 0
                      ? (value: any, index: any) => {
                          return renderHourTick(value, 10);
                        }
                      : undefined
                  }
                  secondXDataKey={secondDataKey()}
                  defaultSelectedLegends={activeLegends}
                  onLegendSelect={(selected: LegendKey[]) =>
                    setActiveLegends(selected)
                  }
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip note={<p>HPPC</p>} text=" ">
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );

  function getLegends() {
    return selectedSuffix.flatMap(({ suffix, displayName, stackSuffix }) =>
      chartKeys.map(
        (key, index) =>
          createChartObject(
            key,
            index,
            suffix,
            displayName,
            stackSuffix,
            selectedInterval,
            selectedChartType as any,
            ExchangeColors
          ) as any
      )
    );
  }
}